import { apiService } from '../utils/api';

export interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  source: 'google' | 'outlook';
  lastSynced: string;
}

export interface SyncResult {
  success: boolean;
  message: string;
  contacts?: Contact[];
  error?: string;
}

class ContactSyncService {
  private static instance: ContactSyncService;
  private googleAuth: unknown = null;
  private outlookAuth: unknown = null;

  private constructor() {
    // Initialize auth clients
    this.initializeGoogleAuth();
    this.initializeOutlookAuth();
  }

  public static getInstance(): ContactSyncService {
    if (!ContactSyncService.instance) {
      ContactSyncService.instance = new ContactSyncService();
    }
    return ContactSyncService.instance;
  }

  private async initializeGoogleAuth() {
    // In development/testing mode, use mock auth
    if (process.env.NODE_ENV === 'development') {
      this.googleAuth = {
        signIn: async () => {
          return {
            success: true,
            token: 'mock-google-token'
          };
        }
      };
      return;
    }

    // TODO: Initialize real Google auth client
  }

  private async initializeOutlookAuth() {
    // In development/testing mode, use mock auth
    if (process.env.NODE_ENV === 'development') {
      this.outlookAuth = {
        signIn: async () => {
          return {
            success: true,
            token: 'mock-outlook-token'
          };
        }
      };
      return;
    }

    // TODO: Initialize real Outlook auth client
  }

  public async syncGoogleContacts(accessToken?: string): Promise<SyncResult> {
    try {
      // In development/testing mode, return mock data
      if (process.env.NODE_ENV === 'development') {
        const mockContacts: Contact[] = [
          {
            id: '1',
            name: 'John Doe',
            email: 'john.doe@gmail.com',
            phone: '+1234567890',
            source: 'google',
            lastSynced: new Date().toISOString()
          },
          {
            id: '2',
            name: 'Jane Smith',
            email: 'jane.smith@gmail.com',
            source: 'google',
            lastSynced: new Date().toISOString()
          }
        ];

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        return {
          success: true,
          message: 'Successfully synced Google contacts',
          contacts: mockContacts
        };
      }

      // Get auth token (either provided or sign in)
      let authToken: string;
      if (accessToken) {
        authToken = accessToken;
        console.log('[ContactSync] Using provided OAuth access token');
      } else {
        const authResult = await this.googleAuth.signIn();
        if (!authResult.success) {
          throw new Error('Failed to authenticate with Google');
        }
        authToken = authResult.token;
      }

      // Call backend API to sync contacts
      const result = await apiService.syncGoogleContacts(authToken);
      return result;
    } catch (error) {
      console.error('Google contact sync error:', error);
      return {
        success: false,
        message: 'Failed to sync Google contacts',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  public async syncOutlookContacts(): Promise<SyncResult> {
    try {
      // In development/testing mode, return mock data
      if (process.env.NODE_ENV === 'development') {
        const mockContacts: Contact[] = [
          {
            id: '3',
            name: 'Bob Wilson',
            email: 'bob.wilson@outlook.com',
            phone: '+1987654321',
            source: 'outlook',
            lastSynced: new Date().toISOString()
          },
          {
            id: '4',
            name: 'Alice Brown',
            email: 'alice.brown@outlook.com',
            source: 'outlook',
            lastSynced: new Date().toISOString()
          }
        ];

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        return {
          success: true,
          message: 'Successfully synced Outlook contacts',
          contacts: mockContacts
        };
      }

      // Get auth token
      const authResult = await this.outlookAuth.signIn();
      if (!authResult.success) {
        throw new Error('Failed to authenticate with Outlook');
      }

      // Call backend API to sync contacts
      const result = await apiService.syncOutlookContacts(authResult.token);
      return result;
    } catch (error) {
      console.error('Outlook contact sync error:', error);
      return {
        success: false,
        message: 'Failed to sync Outlook contacts',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  public async getLastSyncStatus(): Promise<{
    google: { lastSync: string | null; contactCount: number };
    outlook: { lastSync: string | null; contactCount: number };
  }> {
    try {
      const result = await apiService.getContactSyncStatus();
      return result;
    } catch (error) {
      console.error('Failed to get sync status:', error);
      return {
        google: { lastSync: null, contactCount: 0 },
        outlook: { lastSync: null, contactCount: 0 }
      };
    }
  }
}

export const contactSyncService = ContactSyncService.getInstance();
